import React, { Component } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import styled from "styled-components";
import Colors from "Colors";
import ContentWrapper from "Controls/ContentWrapper";
import SurveyContainerWrapper from "./Question/SurveyContainerWrapper";
import Batch from "./Batch";
import HeaderWithProfile from "./HeaderWithProfile";
import { SurveyTitlebar } from "./Question";
import LanguageChangeModal from "./LanguageChangeModal";
import CheckinLanguageSelect from "./CheckinLanguageSelect";

const TitleRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding-right: 20px;
`;

class Batches extends Component {
	constructor(props) {
		super(props);

		this.state = { pendingPrompt: null };
	}

	renderTitleRow() {
		return (
			<TitleRow>
				<SurveyTitlebar culture={this.props.culture} title={this.props.t("Questions")} />
				{this.props.languages && this.props.languages.length > 0 && (
					<CheckinLanguageSelect
						culture={this.props.culture}
						languages={this.props.languages}
						onCultureChange={this.handleCultureSelect}
					/>
				)}
			</TitleRow>
		);
	}

	handleCultureSelect = (culture) => {
		// Ignore language changes while a refetch is in flight, so a second pick
		// can't race the first request's response and leave the list showing a
		// stale language (WIT-12576).
		if (this.props.fetching) {
			return;
		}
		// No prompt when the pick already equals the user's resolved language.
		if (culture === this.props.resolvedCulture) {
			this.props.onCultureChange(culture);
			return;
		}
		this.setState({
			pendingPrompt: {
				context: "survey-list",
				newCulture: culture,
				currentCulture: this.props.resolvedCulture,
			},
		});
	};

	handleUseFromNowOn = () => {
		const { newCulture } = this.state.pendingPrompt;
		this.setState({ pendingPrompt: null });
		this.props.onUseLanguageFromNowOn(newCulture);
	};

	handleKeep = () => {
		const { currentCulture } = this.state.pendingPrompt;
		this.setState({ pendingPrompt: null });
		this.props.onKeepCurrentLanguage(currentCulture);
	};

	handleJustThisVisit = () => {
		const { newCulture } = this.state.pendingPrompt;
		this.setState({ pendingPrompt: null });
		this.props.onUseLanguageThisVisit(newCulture);
	};

	// Closing via the "×" or backdrop is a non-choice: just close the prompt.
	// The page never changed language while the prompt was open (the dropdown
	// still reflects the current language), so there's nothing to revert and
	// no refetch to trigger.
	handleDismiss = () => {
		this.setState({ pendingPrompt: null });
	};

	render() {
		const isWithTemperatureBtn = !this.props.fetching && this.props.widgetEnabled;

		return (
			<SurveyContainerWrapper className="with-auto-height-adjust" paddingTop="80px">
				<ContentWrapper display="block" position="fixed" top="0" width="100%" zIndex="1">
					<HeaderWithProfile
						padding="20px 30px"
						paddingMedia="20px"
						username={this.props.username}
						culture={this.props.culture}
						onLogout={this.props.onLogout}
					/>
				</ContentWrapper>
				<div className="surveyQuestionsBox">
					{this.props.fetching && (
						<div>
							<div className="no-questions">
								<img
									src="/images/logo/logo-animated.gif"
									alt="Loading"
									className="animated-logo"
								/>
							</div>
						</div>
					)}

					{!this.props.fetching && this.props.batches.length === 0 && (
						<React.Fragment>
							{this.renderTitleRow()}
							<ContentWrapper padding="10px">
								<ContentWrapper
									fontWeight="200"
									fontSize="18px"
									textAlign="center"
									padding="20px"
									backgroundColor={Colors.backgroundLightGray}
									borderRadius="3px">
									{this.props.t("ThereAreNoQuestionsToAnswer")}
								</ContentWrapper>
							</ContentWrapper>
						</React.Fragment>
					)}

					{!this.props.fetching && this.props.batches && this.props.batches.length > 0 && (
						<React.Fragment>
							{this.renderTitleRow()}
							<ul className="questionslist">
								{this.props.batches.map((batch) => (
									<Batch
										batch={batch}
										key={batch.id}
										culture={this.props.culture}
										onClick={(clickedBatch) => this.props.onBatchClick(clickedBatch, this.props.culture)}
									/>
								))}
							</ul>
						</React.Fragment>
					)}

					{isWithTemperatureBtn && (
						<ContentWrapper padding="0 10px">
							<div className="show-temperature">
								<a href="#" onClick={this.props.onWidgetClick}>
									{this.props.t("ShowTemperature")}{" "}
									<span className="icon icn-chevron-right"></span>
								</a>
							</div>
						</ContentWrapper>
					)}
				</div>
				{this.state.pendingPrompt && (
					<LanguageChangeModal
						context={this.state.pendingPrompt.context}
						newCulture={this.state.pendingPrompt.newCulture}
						currentCulture={this.state.pendingPrompt.currentCulture}
						languages={this.props.languages}
						onUseFromNowOn={this.handleUseFromNowOn}
						onKeep={this.handleKeep}
						onJustThisVisit={this.handleJustThisVisit}
						onDismiss={this.handleDismiss}
					/>
				)}
			</SurveyContainerWrapper>
		);
	}
}

Batches.propTypes = {
	username: PropTypes.string,
	culture: PropTypes.string,
	languages: PropTypes.array,
	batches: PropTypes.array,
	onLogout: PropTypes.func,
	onCultureChange: PropTypes.func,
	onBatchClick: PropTypes.func,
	onWidgetClick: PropTypes.func,
	fetching: PropTypes.bool,
	widgetsEnabled: PropTypes.bool,
	questionSettings: PropTypes.object,
	resolvedCulture: PropTypes.string,
	savedCulture: PropTypes.string,
	onUseLanguageFromNowOn: PropTypes.func,
	onKeepCurrentLanguage: PropTypes.func,
	onUseLanguageThisVisit: PropTypes.func,
};

export default withTranslation()(Batches);
